/**
 * ChampCouleur — une pastille, un code hexadécimal, une pipette.
 *
 * Né de deux lignes du retour d'usage de septembre :
 *   « Pas de raccourci clavier pour changer la couleur d'une forme (type pipette
 *     sous Illustrator) »
 *   « Impossible de copier un code couleur facilement → fonctionnalité utile pour
 *     récupérer/réutiliser une teinte rapidement »
 *
 * Le code était bien AFFICHÉ à côté de la pastille, mais dans un `<span>` : on ne
 * pouvait ni le sélectionner proprement, ni y coller la teinte d'une charte. Un
 * code couleur qui se lit mais ne se manipule pas ne sert à personne.
 *
 * Trois gestes, donc : cliquer la pastille (le sélecteur natif), écrire ou COLLER
 * un code, et prélever une teinte à l'écran. Plus un bouton pour copier le code
 * en un clic, qui est le geste inverse — celui qu'elle décrit.
 */
import { useEffect, useState } from 'react';
import { normaliserHex, pipetteDisponible, preleverCouleur } from '../utils/couleur';
import { copierDansPressePapiers } from '../utils/pressePapiers';

const AVEC_PIPETTE = pipetteDisponible();

/** Damier de transparence, pour une pastille qui ne porte pas de teinte. */
const DAMIER = {
    backgroundImage: 'linear-gradient(45deg,#6b6b6b 25%,transparent 25%,transparent 75%,#6b6b6b 75%),'
                   + 'linear-gradient(45deg,#6b6b6b 25%,transparent 25%,transparent 75%,#6b6b6b 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 4px 4px',
    backgroundColor: '#3f3f3f',
};

/**
 * @param {object} props
 * @param {string} props.couleur      la teinte courante, sous n'importe quelle écriture
 * @param {(hex:string)=>void} props.onChange  appelé avec un `#RRGGBB` valide, jamais autre chose
 * @param {'normal'|'compact'} [props.taille]
 * @param {string} [props.titre]      infobulle de la pastille
 */
export default function ChampCouleur({ couleur, onChange, taille = 'normal', titre = 'Couleur' }) {
    const hex = normaliserHex(couleur);
    // Une couleur peut légitimement n'être PAS un code : le fond d'un code-barres
    // vaut `transparent` quand on a cliqué le bouton du même nom. Afficher alors
    // un code inventé serait un mensonge visible — on montre le mot tel quel, et
    // la pastille prend un damier pour le dire aussi à l'œil.
    const litteral = hex === null && typeof couleur === 'string' && couleur.trim() ? couleur.trim() : null;
    const valeur = hex || '#888888';
    const affichage = litteral || valeur;
    // Le champ suit la sélection au repos, et la frappe pendant l'édition — sans
    // quoi taper « #F5 » serait réécrit à chaque touche par la couleur courante.
    const [saisie, setSaisie] = useState(affichage);
    const [enEdition, setEnEdition] = useState(false);
    const [copie, setCopie] = useState('repos');   // 'repos' | 'ok' | 'echec'

    useEffect(() => { if (!enEdition) setSaisie(affichage); }, [affichage, enEdition]);

    // La confirmation de copie s'efface d'elle-même ; le minuteur est nettoyé au
    // démontage, sinon un setState tombe sur un composant disparu.
    useEffect(() => {
        if (copie === 'repos') return undefined;
        const t = setTimeout(() => setCopie('repos'), 1400);
        return () => clearTimeout(t);
    }, [copie]);

    const appliquer = (brut) => {
        const nouveau = normaliserHex(brut);
        if (nouveau) onChange(nouveau);
        // Une saisie illisible n'écrase rien : le champ revient à la teinte réelle.
        setSaisie(nouveau || affichage);
        setEnEdition(false);
    };

    const compact = taille === 'compact';
    const cotePastille = compact ? 'w-5 h-5' : 'w-[30px] h-[30px]';
    const arrondiPastille = compact ? 'rounded' : 'rounded-full';

    return (
        <div className="flex items-center gap-2">
            <div className={`${cotePastille} ${arrondiPastille} relative overflow-hidden border-2 border-white/10 shadow-lg shrink-0`}
                 style={litteral ? DAMIER : undefined}>
                <input
                    type="color"
                    value={valeur}
                    onChange={(e) => onChange(normaliserHex(e.target.value) || e.target.value)}
                    className={`absolute inset-0 w-full h-full p-0 cursor-pointer border-0 ${litteral ? 'opacity-0' : ''}`}
                    title={litteral ? `${titre} — actuellement « ${litteral} »` : titre}
                    aria-label={titre}
                />
            </div>

            <input
                type="text"
                value={enEdition ? saisie : affichage}
                spellCheck={false}
                onFocus={(e) => { setEnEdition(true); e.target.select(); }}
                onChange={(e) => setSaisie(e.target.value)}
                onBlur={(e) => appliquer(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); appliquer(e.currentTarget.value); e.currentTarget.blur(); }
                    else if (e.key === 'Escape') { e.preventDefault(); setSaisie(affichage); setEnEdition(false); e.currentTarget.blur(); }
                }}
                // 76 px est la largeur exacte d'un « #FFFFFF » en mono 10 px. Une valeur
                // littérale est plus longue : « TRANSPARENT » y sortait coupé à
                // « TRANSPARE », ce qui est pire que pas de valeur du tout — la capture
                // du banc l'a montré, la lecture du code ne l'aurait jamais dit.
                className={`${litteral ? 'w-[110px]' : 'w-[76px]'} bg-black/30 border border-white/5 rounded px-1.5 py-1 text-[10px] font-mono tracking-widest uppercase text-forge-text/90 outline-none transition-colors hover:bg-white/10 focus:border-forge-accent/50`}
                title="Code hexadécimal — collez-en un pour l'appliquer"
                aria-label="Code couleur hexadécimal"
            />

            <button
                type="button"
                onClick={async () => setCopie(await copierDansPressePapiers(affichage) ? 'ok' : 'echec')}
                className={`shrink-0 w-6 h-6 rounded flex items-center justify-center border transition-colors ${
                    copie === 'ok'    ? 'border-forge-mint/40 text-forge-mint'
                  : copie === 'echec' ? 'border-forge-rose/40 text-forge-rose'
                  :                     'border-white/5 bg-black/30 text-white/40 hover:bg-white/10 hover:text-white'
                }`}
                title={copie === 'echec' ? 'La copie a été refusée par le navigateur' : 'Copier le code couleur'}
                aria-label="Copier le code couleur"
            >
                {copie === 'ok' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ) : copie === 'echec' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                ) : (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                )}
            </button>

            <button
                type="button"
                disabled={!AVEC_PIPETTE}
                onClick={async () => { const preleve = await preleverCouleur(); if (preleve) onChange(preleve); }}
                className="shrink-0 w-6 h-6 rounded flex items-center justify-center border border-white/5 bg-black/30 text-white/40 hover:bg-white/10 hover:text-forge-accent transition-colors disabled:opacity-30 disabled:hover:bg-black/30 disabled:hover:text-white/40 disabled:cursor-not-allowed"
                title={AVEC_PIPETTE
                    ? "Pipette — prélever une teinte n'importe où à l'écran"
                    : 'La pipette demande Chrome ou Edge ; Firefox et Safari ne la proposent pas'}
                aria-label="Pipette"
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/>
                    <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a1 1 0 0 1 0 1.4l-1.8 1.8a1 1 0 0 1-1.4 0l-4.8-4.8a1 1 0 0 1 0-1.4l1.8-1.8a1 1 0 0 1 1.4 0z"/>
                </svg>
            </button>
        </div>
    );
}
